import React from 'react';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import { useGetArticlesQuery } from '../../features/api/apiSlice';
import Image from '../../components/AppImage';
import SEO from '../../components/seo';

const Blog = () => {
  const { data: blogPosts, isLoading, isError } = useGetArticlesQuery();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-neutral-900 font-mono text-xs">
        <span>INITIALIZING RESOURCE RETRIEVAL...</span>
      </div>
    );
  }

  if (isError || !blogPosts) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center text-neutral-900 font-mono text-xs">
        <span className="text-red-600">CRITICAL ERROR: FAILED TO FETCH INTEGRITY NODES.</span>
      </div>
    );
  }

  const featuredPost = blogPosts.find(post => post.isFeatured) || blogPosts[0];

  return (
    <div className="min-h-screen bg-white text-neutral-900 selection:bg-red-50 selection:text-red-900">
      <SEO
        title="Ecosystem Intelligence & Analytics Blog"
        description="Read market notes, rental hunting guidance, and property analysis from HireBroker."
        keywords="real estate blog uganda, rental hunting fees, market analysis, investment tips, property trends, hirebroker data"
        canonicalUrl="https://hirebroker.co/blog"
      />
      <Header />
      
      <main className="pt-24">
        
        {/* SECTION 1: HERO (Pristine White Background) */}
        <section className="bg-white tracking-tight">
          <div className="max-w-6xl mx-auto px-6 pt-16 pb-20">
            <div className="mb-8 opacity-70">
              <Breadcrumbs />
            </div>
            
            <div className="max-w-3xl">
              <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block mb-4">
                Ecosystem Dispatch & Logs
              </span>
              <h1 className="text-4xl sm:text-6xl font-light text-neutral-950 leading-[1.1] mb-6">
                Market intelligence and <span className="font-serif italic text-neutral-500 block sm:inline">structural telemetry.</span>
              </h1>
              <p className="text-lg text-neutral-500 font-light leading-relaxed max-w-xl">
                Verified property metrics, fraud mitigation frameworks, and raw architectural insights derived straight from our active transaction ledgers.
              </p>
            </div>
          </div>
        </section>


        {/* SECTION 2: INTENTIONAL FEATURED NODE (Soft Chalk Grey Background) */}
        {featuredPost && (
          <section className="bg-neutral-50 border-y border-neutral-200/60 py-16">
            <div className="max-w-6xl mx-auto px-6">
              <div className="bg-white border border-neutral-200/60 p-8 sm:p-12 shadow-sm grid lg:grid-cols-12 gap-8 items-center">
                
                <div className="lg:col-span-8 space-y-4">
                  <div className="flex flex-wrap items-center gap-3 text-[10px] uppercase tracking-widest font-mono">
                    {featuredPost.isFeatured && (
                      <span className="bg-red-600 text-white px-2 py-0.5 font-medium">
                        Primary Blueprint
                      </span>
                    )}
                    <span className="text-neutral-400">By {featuredPost.author || 'System Operator'}</span>
                    <span className="text-neutral-400">{featuredPost.readTime || '10 min read'}</span>
                  </div>
                  
                  <h2 className="text-2xl sm:text-3xl font-light text-neutral-950 tracking-tight">
                    {featuredPost.title}
                  </h2>
                  <p className="text-sm text-neutral-500 font-light leading-relaxed max-w-2xl">
                    {featuredPost.excerpt}
                  </p>
                  
                  <div className="pt-2">
                    <button className="w-full lg:w-auto inline-flex items-center justify-center px-6 py-3.5 bg-neutral-950 text-white text-sm font-medium tracking-tight hover:bg-neutral-900 transition-colors duration-150 rounded-none">
                      Read Full Analysis
                    </button>
                  </div>
                </div>

                <div className="lg:col-span-4 w-full aspect-video sm:h-48 bg-neutral-100 border border-neutral-200/60 overflow-hidden rounded-none flex items-center justify-center">
                  {featuredPost.image ? (
                    <Image 
                      src={featuredPost.image} 
                      alt={featuredPost.title} 
                      className="w-full h-full object-cover filter grayscale contrast-125 hover:grayscale-0 transition-all duration-300" 
                    />
                  ) : (
                    <div className="text-center p-6">
                      <span className="text-[10px] uppercase tracking-widest font-mono text-neutral-400 block mb-1">
                        Data Stream Node
                      </span>
                      <p className="text-xs text-neutral-500 font-light">Telemetry Graph Missing</p>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </section>
        )}


        {/* SECTION 3: CORE INTEL METRICS & GRID (Pristine White Background) */}
        <section className="bg-white py-24">
          <div className="max-w-6xl mx-auto px-6">
            
            {/* Category Filter Tabulation Framework */}
            <div className="flex flex-wrap gap-2 mb-16 justify-center border-b border-neutral-100 pb-6">
              {['All Streams', 'Escrow Security', 'Market Analytics', 'Title Verification', 'Infrastructure Tech', 'Risk Allocation'].map((category, idx) => (
                <button
                  key={idx}
                  className={`px-4 py-2 text-xs uppercase tracking-wider transition-all rounded-none border ${
                    idx === 0
                      ? 'bg-neutral-950 border-neutral-950 text-white font-medium'
                      : 'bg-white border-neutral-200 text-neutral-500 hover:text-neutral-950 hover:border-neutral-300'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Trending & Author Matrix Block */}
            <div className="grid gap-8 lg:grid-cols-12 mb-16">
              
              <div className="lg:col-span-7 bg-white border border-neutral-200/80 p-8 rounded-none shadow-sm">
                <span className="text-[10px] uppercase tracking-widest font-mono text-red-600 block mb-4">
                  Active Traffic Threads
                </span>
                <h3 className="text-lg font-medium text-neutral-950 tracking-tight mb-6">Trending Analytical Parameters</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Escrow Lockouts', description: 'Mitigating casual broker deposit leaks.' },
                    { label: 'Title Authentication Loops', description: 'Evaluating registry field access latencies.' },
                    { label: 'Diaspora Capital Shielding', description: 'Eliminating the structural risk of family proxies.' },
                    { label: 'The 10k UGX Velocity Index', description: 'Calculating broadcast conversion efficiencies.' }
                  ].map((topic, i) => (
                    <div key={i} className="border border-neutral-100 bg-neutral-50/50 p-4 rounded-none hover:border-neutral-300 transition-colors">
                      <span className="text-[9px] font-mono text-neutral-400 block mb-1">THREAD_0{i+1}</span>
                      <p className="text-sm font-medium text-neutral-950 mb-1">{topic.label}</p>
                      <p className="text-xs text-neutral-500 font-light">{topic.description}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-5 bg-white border border-neutral-200/80 p-8 rounded-none shadow-sm flex flex-col justify-between">
                <div>
                  <span className="text-[10px] uppercase tracking-widest font-mono text-neutral-400 block mb-4">
                    Verified Registry Contributors
                  </span>
                  <h3 className="text-lg font-medium text-neutral-950 tracking-tight mb-6">Ecosystem Analysts</h3>
                  <div className="space-y-4">
                    {['Kato S.', 'Namubiru J.', 'Akwam O.', 'Mwesigwa D.'].map((author, index) => (
                      <div key={index} className="flex items-center gap-4 pb-3 border-b border-neutral-100 last:border-none last:pb-0">
                        <div className="w-10 h-10 bg-neutral-950 text-white text-xs font-mono flex items-center justify-center rounded-none">
                          [{author.split(' ')[0][0]}]
                        </div>
                        <div>
                          <p className="text-sm font-medium text-neutral-950">{author}</p>
                          <p className="text-xs text-neutral-500 font-light">Senior Land Systems Auditor</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

            </div>

            {/* Main Operational Blog Posts Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogPosts.map((post, index) => (
                <div 
                  key={post.id || index} 
                  className="bg-white border border-neutral-200/40 flex flex-col justify-between hover:border-neutral-300 transition-all shadow-sm group"
                >
                  <div className="aspect-video w-full bg-neutral-100 overflow-hidden border-b border-neutral-200/40">
                    <Image
                      src={post.image || '/public/assets/images/no_image.png'}
                      alt={post.title}
                      className="w-full h-full object-cover filter grayscale contrast-115 group-hover:grayscale-0 transition-all duration-200"
                    />
                  </div>
                  
                  <div className="p-6 flex-1 flex flex-col justify-between space-y-6">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider font-mono">
                        <span className="text-red-600 font-medium">{post.category || 'System Log'}</span>
                        <span className="text-neutral-400">{post.readTime || '6 min read'}</span>
                      </div>
                      
                      <h4 className="text-base font-medium text-neutral-950 tracking-tight group-hover:text-red-600 transition-colors cursor-pointer">
                        {post.title}
                      </h4>
                      
                      <p className="text-sm text-neutral-500 font-light leading-relaxed line-clamp-3">
                        {post.excerpt}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-neutral-100 flex items-center justify-between text-[10px] font-mono text-neutral-400">
                      <span>Operator: {post.author || 'Staff'}</span>
                      <span>{post.date || '2026 Metrics'}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>


        {/* SECTION 4: INTEL PIPELINE REGISTRY TELEMETRY (Warm Muted Grey Background) */}
        <section className="bg-neutral-100/70 py-24 border-t border-neutral-200/50">
          <div className="max-w-3xl mx-auto px-6 text-center space-y-6">
            <span className="text-xs uppercase tracking-[0.2em] text-red-600 font-semibold block">
              Telemetry Data Stream Subscription
            </span>
            <h2 className="text-3xl font-light text-neutral-950 tracking-tight">
              Maintain visibility on structural ecosystem swings.
            </h2>
            <p className="text-sm text-neutral-500 font-light max-w-xl mx-auto leading-relaxed">
              Receive clean data-driven briefs, land registry updates, and raw clearing-house operational insights delivered straight to your communication ledger.
            </p>
            
            <div className="pt-4 max-w-md mx-auto">
              <form onSubmit={(e) => e.preventDefault()} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  placeholder="Enter system endpoint email"
                  required
                  className="flex-1 bg-white px-4 py-3 text-sm border border-neutral-200 text-neutral-900 rounded-none focus:outline-none focus:border-neutral-950 transition-colors shadow-sm placeholder:text-neutral-300"
                />
                <button 
                  type="submit" 
                  className="px-6 py-3 bg-red-600 text-white text-sm font-medium tracking-tight hover:bg-red-700 transition-colors duration-150 rounded-none whitespace-nowrap"
                >
                  Bind Telemetry
                </button>
              </form>
              <span className="text-[10px] font-mono text-neutral-400 block mt-3">
                No telemetry tracking noise • Complete structural data clarity
              </span>
            </div>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default Blog;